<template>
    <v-toolbar
        tag="div"
        class="sub-app-bar"
        height="48"
        :style="style"
    >
        <template
            v-if="currentMembershipChoice"
        >
            <v-btn
                to="/account"
                variant="tonal"
                size="small"
                color="on-secondary"
                class="opacity-100"
            >
                <v-icon
                    :icon="mdiHome"
                    size="24"
                ></v-icon>
                <span
                    v-if="!isMobile"
                    class="ml-1"
                >
                    Account Home
                </span>
            </v-btn>

            <MembershipContextSwitcher
                v-if="membershipChoices.length > 2"
                class="flex-shrink-0"
                :membership-choices="userObj?.membershipChoices"
                :current-membership-choice-uid="currentMembershipUid"
                @on-membership-change="onMembershipChange"
            >
                <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        color="on-secondary"
                        class="opacity-100 text-label-medium"
                        variant="tonal"
                        size="small"
                        title="Switch Membership"
                        slim
                    >
                        <v-icon
                            :icon="mdiSwapHorizontal"
                            size="24"
                        ></v-icon>
                        <span
                            v-if="!isMobile"
                            class="ml-1"
                        >
                            Switch Account
                        </span>
                        <v-icon
                            size="26"
                            :icon="mdiMenuDown"
                        ></v-icon>
                    </v-btn>
                </template>
            </MembershipContextSwitcher>
            <MembershipContextTypeToggleBtn
                v-else-if="membershipChoices.length === 2"
                variant="tonal"
                size="small"
                color="on-secondary"
                class="opacity-100 text-label-medium"
                :membership-choices="userObj?.membershipChoices"
                :current-membership-choice="currentMembershipChoice"
                @on-membership-change="onMembershipChange"
            >
                <template #default="{ title }">
                    <v-icon
                        :icon="mdiSwapHorizontal"
                        size="24"
                    ></v-icon>
                    <span
                        v-if="!isMobile"
                        class="ml-1"
                    >
                        {{ title }}
                    </span>
                </template>
            </MembershipContextTypeToggleBtn>

            <v-spacer></v-spacer>

            <!-- Member Role -->
            <v-chip
                v-if="currentMembershipChoice?.membership?.role?.name && !isWidthXs"
                size="x-small"
                variant="outlined"
                class="flex-shrink-0 text-uppercase font-weight-medium"
                label
            >
                {{ currentMembershipChoice.membership.role.name }}
            </v-chip>

            <!-- Organization Logo (or name) -->
            <v-chip
                v-if="isPersonalNoMembership"
                class="font-weight-medium"
                size="small"
                variant="tonal"
            >
                PERSONAL
            </v-chip>
            <div
                v-else-if="logoObj"
                class="d-flex align-center h-100 w-auto pa-1"
            >
                <div
                    class="sub-app-bar-logo d-flex w-auto pa-1 bg-white rounded"
                    :alt="orgName"
                    :style="{
                        height: `${logoHeight}px`
                    }"
                >
                    <img
                        class="h-100 w-auto"
                        :src="logoObj.url"
                        :alt="orgName"
                    />
                </div>
            </div>
            <h2
                v-else-if="orgName"
                class="flex-grow-0 flex-shrink-1 text-truncate text-title-large py-0 my-0"
            >
                {{ orgName }}
            </h2>
            <div
                v-else
            >
                <!-- PLACEHOLDER FOR MEMBERSHIP AND NO ORG NAME OR LOGO -->
            </div>
        </template>
    </v-toolbar>
</template>

<script lang="ts" setup>
    import {
        useUser,
        useIsMobile,
        useAccountUtils,
        useMembershipSwitch
    } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import type {
        Logo
    } from '~/types';
    import {
        mdiSwapHorizontal,
        mdiHome,
        mdiMenuDown
    } from '@mdi/js';
    import {
        LookupKeys
    } from '~/constants';
    import { isError as lo_isError } from 'es-toolkit';
    import { useDisplay } from 'vuetify';
    import { SiteMembershipContext } from '~/types';


    const MembershipContextTypeToggleBtn = defineAsyncComponent(() => import('~/components/controls/MembershipContextTypeToggleBtn/MembershipContextTypeToggleBtn.vue'));
    const MembershipContextSwitcher = defineAsyncComponent(() => import('~/components/account/account-shared/MembershipContextSwitcher/MembershipContextSwitcher.vue'));

    const {
        qsEntitySwitch,
        qsEntitySwitchNull
    } = LookupKeys;
    const {
        isLoading,
        start: loaderStart,
        finish: loaderStop
    } = useLoadingIndicator();
    const {
        userObj,
        currentMembershipChoice,
        currentMembershipUid,
        setMembershipUid
    } = useUser({ siteContext: 'menu' });
    const {
        accountSectionPath,
        isInAccountSection
    } = useAccountUtils();
    const {
        isMobile
    } = useIsMobile();
    const {
        xs: isWidthXs,
        name: displayName
    } = useDisplay();
    const {
        emitMembershipSwitch
    } = useMembershipSwitch();

    const createGlobalAlert = useGlobalAlert();
    const orgName = computed(() => currentMembershipChoice.value?.membership?.entity?.name);
    const logoObj = computed<Nilish<Logo>>(() => currentMembershipChoice.value?.membership?.entity?.brand?.primaryLogo);
    const isPersonalNoMembership = computed(() => !currentMembershipChoice.value?.membership && currentMembershipChoice.value?.context === SiteMembershipContext.Personal);

    const style = computed<Undefinable<Record<string, string>>>(() => {
        const entity = currentMembershipChoice.value?.membership?.entity;
        let color = 'rgb(var(--v-theme-surface))';
        let backgroundColor = 'rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity))';

        if (entity?.brand) {
            let { primaryColor, textColor } = entity.brand;
            color = textColor || color;
            backgroundColor = primaryColor || backgroundColor;
        }

        return {
            color,
            backgroundColor
        };
    });
    const membershipChoices = computed(() => userObj.value?.membershipChoices || []);
    const logoHeight = computed<number>(() => {
        switch (displayName.value) {
            case 'sm':
            case 'xs':
                return 28;
            default:
                return 32;
        }
    });

    async function onMembershipChange(uid: Nilish<string>): Promise<void> {
        if (isInAccountSection()) {
            navigateTo({
                path: accountSectionPath,
                query: {
                    [qsEntitySwitch]: uid
                }
            }, {
                replace: true
            });

            return;
        }

        uid = (uid === qsEntitySwitchNull) ? null : uid ?? null;
        loaderStart();
        const response = await setMembershipUid(uid);
        loaderStop();

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Unable to switch memberships. Please reload page and try again.',
                show: true,
                type: 'error'
            });

            return;
        }

        emitMembershipSwitch();
    }
</script>

<style lang="scss">
@use '~GlobalVars' as vars;

.sub-app-bar {
    // Constrain the bar's content to the page max width + padding (matches
    // SuperAppBar and ContainerContent) so it aligns with the header and page
    // margins. The bar's background stays full-width.
    .v-toolbar__content {
        max-width: vars.$bp-max;
        width: 100%;
        margin-inline: auto;
        padding-block: 0;
        padding-inline: 16px;
        gap: 8px;

        @media (min-width: 960px) {
            padding-inline: 24px;
        }
    }
}
</style>
